import {
  EAsyncEndTags,
  type TRunWithPayload,
  type TUseResponse,
  errorResult,
  successResult,
} from 'pullstate';
import { startTransition, useCallback, useMemo, useRef, useState } from 'react';

import { useEffectOnce } from '@/renderer/utils/hooks/useEffectOnce';
import { useUpdateEffect } from '@/renderer/utils/hooks/useUpdateEffect';
import type { RequestSchema } from '@/types/request';

import { webSocketWatcherEventTarget } from '@/components/UI/WebSocketWatcher';
import { PullstateCore } from '../store';
import { request } from './api';
import { injectIntoInfiniteData2 } from './api/infinite';
import type {
  UseInfiniteRequestOpts,
  UseInfiniteRequestResult,
  UseRequestOpts,
  WithNew,
} from './api/types';
import { PollingDelaysDefault, isNewDbItem } from './api/types';
import { RequestError } from './errors';
import { useAsyncOrLayoutEffect } from './hooks/useAsyncOrLayoutEffect';
import { useDeepMemo } from './hooks/useDeepMemo';
import { useRefAutoUpdate, useRefUpdating } from './hooks/useRefUpdating';
import { IS_SERVER, useLayoutEffectOrEffect } from './ssr';
import { useAuthToken } from './useMe';
import useOnlineStatus from './useOnlineStatus';
import { usePageContext } from './usePageContext';
import useWebSocket, { WS_BASE_URL } from './useWebSocket2';
import type { Subscription } from './ws/types';

const ID = 'id';
const DEFAULT_BY = ID;
const DEFAULT_DIR = 'DESC';
const LIMIT = 50;
const POLL_LIMIT = 10;
const _BASE_PARAM = { cursor: 0 as number | undefined, limit: LIMIT };

const POLLING_RUNNING_INITIAL: number | null = null;

// Between these hours, use the extra long MAX pause
const BUSINESS_HOURS_START = 5;
const BUSINESS_HOURS_END = 23;

const ptTime = new Intl.DateTimeFormat('en-US', {
  timeZone: 'America/Los_Angeles',
  hour: 'numeric',
  hour12: false,
  hourCycle: 'h23',
});

const JITTER = 500;

const InfiniteRequest = PullstateCore.createAsyncAction(
  async <
    const ItemType extends API.DbItem,
    const BodyType = void,
    const QueryType extends API.PageInput<ItemType> = API.PageInput<ItemType>,
    const MetaType = void,
    const FromSocketType = ItemType,
  >([opts, knownIds]: [
    UseInfiniteRequestOpts<ItemType, BodyType, QueryType, MetaType, FromSocketType>,
    Set<API.Id>,
  ]) => {
    const { req, initialQuery, query, pause } = opts;

    if (pause) {
      return successResult(null);
    }

    const orderParam = initialQuery?.order || query?.order;
    const by = orderParam?.by || DEFAULT_BY;
    const dir = orderParam?.dir || DEFAULT_DIR;
    const pageParam = (initialQuery || query)?.page;
    const initPageDirection = dir === 'DESC' ? ('after' as const) : ('before' as const);
    const initCursor = pageParam?.[initPageDirection];

    const initialOpts = {
      query: {
        ...(initialQuery || query),
        order: {
          ...orderParam,
          by,
          dir,
        },
        page: {
          ...(initialQuery?.page || query?.page),
          limit: pageParam?.limit || LIMIT,
          [initPageDirection]: initCursor,
        },
      },
    } as UseRequestOpts<API.WithPage<ItemType[], MetaType>, QueryType>;

    const reqo: RequestSchema<
      API.WithPage<ItemType[], MetaType>,
      BodyType,
      RequestInfo,
      QueryType
    > = typeof req === 'string' ? { input: req } : req;

    try {
      const json: API.WithPage<ItemType[], MetaType> = await request(
        {
          // ...restOpts,
          ...reqo,
          init: {
            ...reqo.init,
            signal: reqo.init?.signal,
          },
        },
        {
          // body: strProps,
          noStringify: true,
          query: initialOpts.query,
          // hideAlert: opts?.hideAlert,
          // skipRefresh: opts?.skipRefresh,
          fetch: IS_SERVER ? opts.fetch : undefined, // SSR fetch includes headers from page request
        },
      );

      knownIds.clear();
      for (const item of json.data || []) {
        knownIds.add(item.id);
      }

      return successResult(json);
    } catch (error) {
      return errorResult(
        [error instanceof RequestError ? EAsyncEndTags.RETURNED_ERROR : EAsyncEndTags.THREW_ERROR],
        (error instanceof Error && error.message) ||
          (error as Error)?.toString?.() ||
          `${error || 'Error'}`,
        error,
      );
    }
  },
  {
    actionId: '∞',
    subsetKey: <
      const ItemType extends API.DbItem,
      const BodyType = void,
      const QueryType extends API.PageInput<ItemType> = API.PageInput<ItemType>,
      const MetaType = void,
      const FromSocketType = ItemType,
    >(
      opts: UseInfiniteRequestOpts<ItemType, BodyType, QueryType, MetaType, FromSocketType>,
    ) => {
      return [opts.key, JSON.stringify(opts.initialQuery || opts.query), JSON.stringify(opts.req)];
    },
  },
);

export function useInfiniteRequest<
  const ItemType extends API.DbItem,
  const BodyType = void,
  const QueryType extends API.BasePageInput = API.WithPageInput<ItemType>,
  const MetaType = void,
  const FromSocketType = ItemType,
>(
  opts: UseInfiniteRequestOpts<ItemType, BodyType, QueryType, MetaType, FromSocketType>,
): UseInfiniteRequestResult<ItemType, MetaType> {
  const {
    req,
    event: eventName,
    eventValidate,
    cursorKey,
    initialQuery: _initialQuery, // @todo should probably use this
    key: baseKey,
    serverSkip,
    skipPoll,
    query: baseQuery,
    pause,
    PollingDelays = PollingDelaysDefault,
    holdPrevious,
  } = opts;
  const pageContext = usePageContext();

  // Initial request (via Pullstate Hook)

  const modifiedBaseQuery = useMemo(() => {
    const by = baseQuery?.order?.by;
    const dir = baseQuery?.order?.dir;

    return by && dir
      ? baseQuery
      : {
          ...baseQuery,
          order: {
            by: by || DEFAULT_BY,
            dir: dir || DEFAULT_DIR,
          },
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseQuery, baseQuery?.order?.by, baseQuery?.order?.dir]);
  const query = useDeepMemo(modifiedBaseQuery);
  const key = useMemo(() => {
    return baseKey.includes(':') ? baseKey : (`${baseKey}:${JSON.stringify(query)}` as const);
  }, [baseKey, query]);
  const [_isMountedState, setIsMountedState] = useState(false);
  const isMounted = useRef(false);
  const keyRef = useRef(key);
  const knownIds = useRef<Set<API.Id>>(new Set());
  const [isFetchingPreviousPage, setIsFetchingPreviousPage] = useState(false);
  const [isFetchingNextPage, setIsFetchingNextPage] = useState(false);

  const result = InfiniteRequest.use(
    [
      IS_SERVER && 'fetch' in pageContext ? { ...opts, fetch: pageContext.fetch } : opts,
      knownIds.current,
    ],
    {
      key,
      ssr: true,
      initiate: IS_SERVER && serverSkip ? false : !pause,
      dormant: !isMounted.current,
      // cacheBreak: !isMounted.current || keyRef.current !== key,
      holdPrevious: holdPrevious === undefined ? key === keyRef.current : holdPrevious,
    },
    // @TODO biome-ignore lint/suspicious/noExplicitAny:
  ) as unknown as TUseResponse<any, API.WithPage<ItemType[], void>, string>;

  const queryRef = useRefUpdating(query);

  if (keyRef.current !== key) {
    keyRef.current = key;
    queryRef.current = query;
  }

  const execute = useRefUpdating(result.execute);
  const updateCached = useRefUpdating(result.updateCached);

  // biome-ignore lint/correctness/useExhaustiveDependencies:
  useUpdateEffect(() => {
    // result.clearCached();
  }, [key, typeof req === 'string' ? req : req.input]);

  // Wait for Pullstate hook to resolve

  useAsyncOrLayoutEffect([key], async () => {
    if (IS_SERVER) {
      // biome-ignore lint/correctness/useHookAtTopLevel: This looks conditional but it actually isn't
      await PullstateCore.useInstance().resolveAsyncState();
    }
  });

  // Pagination info

  const by = query?.order?.by || DEFAULT_BY;
  const dir = query?.order?.dir || DEFAULT_DIR;
  const pageParam = query?.page;
  const initPageDirection = dir === 'DESC' ? ('after' as const) : ('before' as const);
  const cursor = pageParam?.[initPageDirection];

  // Next Page
  const nextCursorVal = result.payload?.pageInfo.endCursor;
  const nextCursorIdVal = result.payload?.pageInfo.endCursorId;
  const nextPage = useRef({
    cursor: nextCursorVal,
    cursorId: nextCursorIdVal,
    running: false,
    has: !!result.payload?.pageInfo.hasNextPage,
  });

  useRefAutoUpdate(nextPage, 'cursor', nextCursorVal);
  useRefAutoUpdate(nextPage, 'cursorId', nextCursorIdVal);
  useRefAutoUpdate(nextPage, 'has', !!result.payload?.pageInfo.hasNextPage);

  // Prev Page
  const prevCursorVal = result.payload?.pageInfo.startCursor;
  const prevCursorIdVal = result.payload?.pageInfo.startCursorId;
  const prevPage = useRef({
    cursor: prevCursorVal,
    cursorId: prevCursorIdVal,
    running: false,
    has: !!result.payload?.pageInfo.hasPreviousPage,
  });

  useRefAutoUpdate(prevPage, 'cursor', prevCursorVal);
  useRefAutoUpdate(prevPage, 'cursorId', prevCursorIdVal);
  useRefAutoUpdate(prevPage, 'has', !!result.payload?.pageInfo.hasPreviousPage);

  const reqRef = useRef(req);

  if (reqRef.current !== req) {
    reqRef.current = req;
    queryRef.current = query;
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies:
  const fetchPage = useCallback(
    async (
      pageDirection: 'before' | 'after',
      pageCursorKey: 'endCursor' | 'startCursor',
      fetchCursor: typeof cursor,
    ) => {
      const pageCursorIdKey = `${pageCursorKey}Id` as const;
      const curKey = keyRef.current;

      const initialOpts = {
        query: {
          ...queryRef.current,
          order: {
            by,
            dir,
            ...queryRef.current?.order,
          },
          page: {
            limit: pageParam?.limit || LIMIT,
            ...queryRef.current?.page,
            after: undefined, // unset these from queryRef
            before: undefined,
            [pageDirection]: fetchCursor, // and only set one of them
          },
        },
      } as UseRequestOpts<API.WithPage<ItemType[], MetaType>, QueryType>;

      const reqo: RequestSchema<
        API.WithPage<ItemType[], MetaType>,
        BodyType,
        RequestInfo,
        QueryType
      > = typeof reqRef.current === 'string' ? { input: reqRef.current } : reqRef.current;

      const json: API.WithPage<ItemType[], MetaType> = await request(
        {
          ...reqo,
          init: {
            ...reqo.init,
            signal: reqo.init?.signal,
          },
        },
        {
          noStringify: true,
          query: initialOpts.query,
          fetch: IS_SERVER ? opts.fetch : undefined,
        },
      );

      const hasData = json.data?.length;

      if (hasData || json.pageInfo.hasNextPage !== result.payload?.pageInfo.hasNextPage) {
        startTransition(() => {
          updateCached.current(
            (draft, original) => {
              if (hasData) {
                injectIntoInfiniteData2(
                  json.data,
                  original,
                  draft,
                  knownIds,
                  cursorKeyRef.current,
                  undefined,
                  pageDirection === 'after' ? 'end' : 'start',
                );
              }

              const nextPageChanged = json.pageInfo.hasNextPage !== draft.pageInfo.hasNextPage;
              const totalChanged =
                json.pageInfo.total && json.pageInfo.total !== draft.pageInfo.total;
              const cursorChanged =
                json.pageInfo[pageCursorKey] &&
                json.pageInfo[pageCursorKey] !== draft.pageInfo[pageCursorKey];
              const cursorIdChanged =
                json.pageInfo[pageCursorIdKey] &&
                json.pageInfo[pageCursorIdKey] !== draft.pageInfo[pageCursorIdKey];

              if (nextPageChanged) {
                draft.pageInfo.hasNextPage = json.pageInfo.hasNextPage;
              }

              if (totalChanged) {
                draft.pageInfo.total = json.pageInfo.total;
              }

              if (cursorChanged) {
                (draft as API.WithPage<ItemType[], void>).pageInfo[pageCursorKey] =
                  json.pageInfo[pageCursorKey];
              }

              if (cursorIdChanged) {
                (draft as API.WithPage<ItemType[], void>).pageInfo[pageCursorIdKey] =
                  json.pageInfo[pageCursorIdKey];
              }

              nextPage.current.has = !!json.pageInfo.hasNextPage;

              // draft.meta = json.meta || draft.meta;
            },
            { resetTimeCached: true, key: curKey, notify: isMounted.current },
          );
        });
      }

      return [json.pageInfo[pageCursorKey], json.pageInfo[pageCursorIdKey]];
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [],
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies:
  const fetchNextPage = useCallback(async () => {
    if (nextPage.current.running || !nextPage.current.has) {
      return;
    }

    nextPage.current.running = true;

    try {
      setIsFetchingNextPage(true);

      const [fetchedCursor, fetchedCursorId] = await fetchPage(
        'after',
        'endCursor',
        ((queryRef.current?.order?.by || by) !== ID
          ? [nextPage.current.cursor, nextPage.current.cursorId]
          : nextPage.current.cursor) || cursor,
      );

      nextPage.current.cursor = fetchedCursor as any;
      nextPage.current.cursorId = fetchedCursorId as any;

      nextPage.current.running = false;
      setIsFetchingNextPage(false);
    } catch (error) {
      nextPage.current.running = false;
      setIsFetchingNextPage(false);
      throw error;
    }
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies:
  const fetchPreviousPage = useCallback(async () => {
    if (prevPage.current.running || !prevPage.current.has) {
      return;
    }

    prevPage.current.running = true;

    try {
      setIsFetchingPreviousPage(true);

      const [fetchedCursor, fetchedCursorId] = await fetchPage(
        'before',
        'startCursor',
        (queryRef.current?.order?.by !== ID
          ? [prevPage.current.cursor, prevPage.current.cursorId]
          : prevPage.current.cursor) || cursor,
      );

      prevPage.current.cursor = fetchedCursor as any;
      prevPage.current.cursorId = fetchedCursorId as any;

      prevPage.current.running = false;
      setIsFetchingPreviousPage(false);
    } catch (error) {
      prevPage.current.running = false;
      setIsFetchingPreviousPage(false);
      throw error;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refetch = useCallback(async () => {
    const response = await execute.current({
      key: keyRef.current,
    });

    return response;
  }, []);

  const reset = useCallback(() => {
    console.warn('not yet implemented');
  }, []);

  //
  // Websocket shenanigans
  //

  const authToken = useAuthToken();
  const isOnline = useOnlineStatus();
  const pollingDelay = useRef(PollingDelays.WS_CONNECTED_START);

  // Event validator fn
  const eventValidateRef = useRefUpdating(eventValidate);

  if (IS_SERVER && result.payload?.data.length) {
    for (const item of result.payload.data) {
      knownIds.current.add(item.id);
    }
  }

  // Cursor key
  const cursorKeyRef = useRefUpdating<keyof ItemType>(cursorKey || ID);

  // Message changed updater
  const onInfiniteUpdated = useMemo(
    () => {
      return async (event: MessageEvent, ojson: API.WebsocketResult<API.Id>) => {
        const curKey = keyRef.current;
        const rjson = (await eventValidateRef.current?.(event, ojson, knownIds)) as
          | false
          | API.SubscriptionResponse<ItemType>;

        if (rjson === false) {
          return;
        }

        if (typeof rjson === 'number') {
          // Still a number, fetch from DB
          console.warn('Received update from WS but eventValidate did not fetch the data');

          return;
        }

        const json = rjson || ojson;

        const newItem = json.data;
        const isInSet = knownIds.current.has(newItem.id);

        if (isInSet) {
          // We have this in our cache somewhere. Update it.
          startTransition(() => {
            updateCached.current(
              (oldData) => {
                for (const entryIndex in oldData.data) {
                  const entry = oldData.data[entryIndex];

                  if (entry.id === newItem.id) {
                    // Same item, replace it
                    oldData.data.splice(+entryIndex, 1, {
                      ...entry,
                      ...newItem,
                    });
                  }
                }
              },
              { resetTimeCached: true, key: curKey, notify: isMounted.current },
            );
          });
        }
      };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  // Message received injector
  const onInfiniteCreated = useCallback<Subscription<WithNew<ItemType>, string, FromSocketType>>(
    async (event, ojson) => {
      if (isNewDbItem(ojson.data)) {
        ojson.data.isNew = true;
      }

      const curKey = keyRef.current;
      const res = await eventValidateRef.current?.(event, ojson, knownIds);

      if (res === false) {
        return;
      }

      const json = (res as API.SubscriptionResponse<ItemType>) || ojson;

      // startTransition(() => {
      updateCached.current(
        (draft, original) => {
          injectIntoInfiniteData2(
            [json.data as ItemType],
            original,
            draft,
            knownIds,
            cursorKeyRef.current,
            1,
            queryRef.current.order?.dir === 'DESC' ? 'start' : 'end',
          );
        },
        { resetTimeCached: true, key: curKey, notify: isMounted.current },
      );
      // });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const ws = useWebSocket(WS_BASE_URL, {
    connect: !!eventName && !!authToken,
    subscriptions: eventName
      ? {
          [`${eventName}/updated`]: onInfiniteUpdated,
          [`${eventName}/created`]: onInfiniteCreated,
        }
      : undefined,
  });

  const wsReady = useRef(ws.ready);

  const PollingDelaysRef = useRefUpdating(PollingDelays);

  // biome-ignore lint/correctness/useExhaustiveDependencies(isOnline):
  // biome-ignore lint/correctness/useExhaustiveDependencies(PollingDelays):
  useLayoutEffectOrEffect(() => {
    if (ws.ready) {
      if (pollingDelay.current < PollingDelaysRef.current.WS_CONNECTED_START) {
        pollingDelay.current = PollingDelaysRef.current.WS_CONNECTED_START;
      }
    } else {
      pollingDelay.current = PollingDelaysRef.current.START;
    }

    wsReady.current = ws.ready;
  }, [ws.ready, isOnline, PollingDelays]);

  //
  // End of websocket shenanigans
  //

  //
  // Polling thingamajig
  //

  const isPollingRunning = useRef(POLLING_RUNNING_INITIAL);
  const pollingCursor = useRef<API.WithPageInfo<ItemType>['pageInfo']['startCursor']>(undefined);
  const pollingCursorId = useRef<API.Id>(0);
  const pollingTimer = useRef<NodeJS.Timeout>();

  // We should periodically poll for new messages. Though less often when WS is connected; it's just a backup.
  // biome-ignore lint/correctness/useExhaustiveDependencies:
  const loop = useCallback(async () => {
    if (isPollingRunning.current || !isMounted.current || skipPoll) {
      // Prevent having this run multiple times
      return;
    }

    const curKey = keyRef.current;
    const pollingId = Math.random();

    isPollingRunning.current = pollingId;

    const currentHour = +ptTime.format(new Date());
    const isOob = currentHour < BUSINESS_HOURS_START || currentHour > BUSINESS_HOURS_END;

    try {
      const cursorCurrent = pollingCursor.current;
      const cursorCurrentId = pollingCursorId.current;

      const res = await request<API.WithPage<WithNew<ItemType>[], MetaType>, BodyType, QueryType>(
        reqRef.current,
        {
          query: {
            ...queryRef.current,
            order: {
              by: queryRef.current.order?.by || ID,
              dir:
                cursorCurrent !== undefined && cursorCurrent !== null
                  ? (queryRef.current.order?.dir === 'DESC' ? 'ASC' : 'DESC') || 'ASC'
                  : queryRef.current.order?.dir || 'DESC',
            },
            page: {
              [cursorCurrent ? 'after' : 'before']:
                cursorCurrent === null || cursorCurrent === undefined
                  ? undefined
                  : [cursorCurrent, cursorCurrentId],
              limit: POLL_LIMIT,
            },
          },
        } as unknown as QueryType,
      );

      if (isPollingRunning.current !== pollingId || !isMounted.current) {
        return; // short circuit in case something went weird
      }

      if (res.data.length) {
        // We found new data! Inject it in
        const newData: WithNew<ItemType>[] = [];

        // Update cursorCurrent
        const pageCursor = res.pageInfo[cursorCurrent ? 'endCursor' : 'startCursor'];
        const pageCursorId = res.pageInfo[cursorCurrentId ? 'endCursorId' : 'startCursorId'];
        const nextCursor = pageCursor ? pageCursor : res.data[0].id || cursorCurrent;
        const nextCursorId = pageCursorId ? pageCursorId : res.data[0].id || cursorCurrentId;

        if (nextCursor !== cursorCurrent) {
          pollingCursor.current = nextCursor as any;
        }

        if (nextCursorId > cursorCurrentId) {
          pollingCursorId.current = nextCursorId;
        }

        for (const item of res.data) {
          // Avoid duplicates
          if (!knownIds.current.has(item.id)) {
            knownIds.current.add(item.id);
            newData[cursorCurrent ? 'unshift' : 'push'](item);
          }
        }

        if (newData.length) {
          res.data = newData;

          startTransition(() => {
            updateCached.current(
              (draft, original) => {
                const newItems = injectIntoInfiniteData2(
                  newData,
                  original,
                  draft,
                  knownIds,
                  cursorKeyRef.current,
                  POLL_LIMIT,
                  queryRef.current.order?.dir === 'DESC' ? 'start' : 'end',
                );

                if (eventName && newItems?.length) {
                  for (const newItem of newItems) {
                    webSocketWatcherEventTarget.dispatchEvent(
                      new CustomEvent('ras:webSocketWatcher/created', {
                        detail: {
                          event: `${eventName}/created`,
                          data: newItem,
                        },
                      }),
                    );
                    newItem;
                  }
                }
              },
              { resetTimeCached: true, key: curKey, notify: isMounted.current },
            );
          });

          if (wsReady.current) {
            pollingDelay.current = PollingDelaysRef.current.WS_CONNECTED_JUST_RECEIVED;
          } else {
            pollingDelay.current = PollingDelaysRef.current.JUST_RECEIVED;
          }
        }
      } else {
        // Data is empty; that means we're up to date, so use the latest cursorCurrent from websocket
        const nextCursor =
          result.payload?.data.length &&
          result.payload.pageInfo[cursorCurrent ? 'endCursor' : 'startCursor'];
        const nextCursorId =
          result.payload?.data.length &&
          result.payload.pageInfo[cursorCurrent ? 'endCursorId' : 'startCursorId'];

        if (nextCursor && nextCursor !== cursorCurrent) {
          pollingCursor.current = nextCursor;
        }

        if (nextCursorId && nextCursorId > cursorCurrentId) {
          pollingCursorId.current = nextCursorId;
        }

        if (wsReady.current) {
          pollingDelay.current = Math.min(
            pollingDelay.current + PollingDelaysRef.current.WS_CONNECTED_INCREMENT,
            isOob
              ? PollingDelaysRef.current.WS_CONNECTED_MAX_OOB
              : PollingDelaysRef.current.WS_CONNECTED_MAX,
          );
        } else {
          pollingDelay.current = Math.min(
            pollingDelay.current + PollingDelaysRef.current.INCREMENT,
            PollingDelaysRef.current.MAX,
            isOob ? PollingDelaysRef.current.MAX_OOB : PollingDelaysRef.current.MAX,
          );
        }
      }
    } catch (error) {
      console.warn(error);

      if (wsReady.current) {
        pollingDelay.current = Math.min(
          pollingDelay.current + PollingDelaysRef.current.WS_CONNECTED_INCREMENT,
          isOob
            ? PollingDelaysRef.current.WS_CONNECTED_MAX_OOB
            : PollingDelaysRef.current.WS_CONNECTED_MAX,
        );
      } else {
        pollingDelay.current = Math.min(
          pollingDelay.current + PollingDelaysRef.current.ERROR_INCREMENT,
          isOob ? PollingDelaysRef.current.MAX_OOB : PollingDelaysRef.current.MAX,
        );
      }
    }

    clearTimeout(pollingTimer.current); // just in case another one started up
    isPollingRunning.current = null;

    if (isMounted.current) {
      // Only keep running if still mounted
      pollingTimer.current = setTimeout(
        loop,
        pollingDelay.current + Math.round(Math.random() * JITTER),
      );
    }
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies(PollingDelays):
  useLayoutEffectOrEffect(() => {
    if (!IS_SERVER) {
      pollingTimer.current = setTimeout(
        loop,
        pollingDelay.current + Math.round(Math.random() * JITTER),
      );

      return () => {
        clearTimeout(pollingTimer.current);
      };
    }
  }, [loop, PollingDelays]);

  //
  // End of polling
  //

  useLayoutEffectOrEffect(() => {
    isMounted.current = true;

    return () => {
      isMounted.current = false;
    };
  }, []);

  useAsyncOrLayoutEffect([key], async () => {
    if (!IS_SERVER) {
      const response = await execute.current({
        key,
      });

      nextPage.current.has = !!response.payload?.pageInfo.hasNextPage;
    }
  });

  useEffectOnce(() => {
    setIsMountedState(true);
  });

  return {
    error: result.errorPayload,
    hasNextPage: !!result.payload?.pageInfo?.hasNextPage,
    hasPreviousPage: false, // result.data?.pageInfo.hasPreviousPage
    isError: !result.isFailure,
    isFetching: result.isLoading || result.isUpdating,
    isFetchingNextPage,
    isFetchingPreviousPage,
    isLoading: result.isLoading,
    isPending: !result.isFinished,
    isPlaceholderData: false,
    isRefetching: false,
    isSuccess: result.isSuccess,
    renderPayload: result.renderPayload as TRunWithPayload<API.WithPage<ItemType[], MetaType>>,
    data: result.payload as typeof result.payload extends null
      ? null
      : API.WithPage<ItemType[], MetaType>,
    fetchNextPage,
    fetchPreviousPage,
    refetch,
    reset,
    newestItem: result.payload?.data?.[0],
    oldestItem: result.payload?.data?.[result.payload?.data.length - 1],
    isEmpty:
      !result.payload?.data?.[0] && !result.payload?.data?.[result.payload?.data?.length - 1],
    wsConnected: ws.ready,
  };
}
